import { useState, useEffect, useCallback } from 'react';
import {
  getDomainStatus,
  setCustomDomain,
  getDnsInstructions,
  verifyDomain,
  removeDomain,
  getPurchasedDomains,
  updateDomainAutoRenew,
  type DomainStatus as DomainStatusData,
  type DnsInstructions,
  type PurchasedDomain,
} from '../api/domains';

export function DomainStatus() {
  const [domain, setDomain] = useState<DomainStatusData | null>(null);
  const [purchasedDomains, setPurchasedDomains] = useState<PurchasedDomain[]>([]);
  const [instructions, setInstructions] = useState<DnsInstructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [showDomainInput, setShowDomainInput] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState<string | null>(null);
  const [pollingStopped, setPollingStopped] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusData, purchasedData] = await Promise.all([
        getDomainStatus(),
        getPurchasedDomains().catch(() => ({ success: false, domains: [] as PurchasedDomain[] })),
      ]);
      setDomain(statusData.domain);
      setPurchasedDomains(purchasedData.domains || []);
      // 'verified' means DNS is right and Cloudflare is still issuing the
      // certificate. Keep the records on screen through that wait, and through
      // 'verifying', so the panel is never blank while the customer waits.
      const awaitingSetup = ['pending', 'verifying', 'verified'].includes(
        statusData.domain.verificationStatus || ''
      );
      if (statusData.domain.customDomain && awaitingSetup) {
        const instrData = await getDnsInstructions();
        setInstructions(instrData.instructions);
      }
    } catch (err) {
      console.error('Failed to load domain status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  /**
   * Poll while the domain is mid-setup.
   *
   * The cron reconciles a verified domain to connected on its own, and the
   * panel tells the customer as much — "the certificate is being issued, this
   * needs nothing further from you". Without polling that promise was false in
   * the only way the customer can see: the backend healed itself and the screen
   * they were watching never changed, so the honest reading was that it had
   * stuck.
   *
   * Stops on any terminal state, and gives up after twenty minutes rather than
   * polling a forgotten tab indefinitely.
   */
  const status = domain?.verificationStatus ?? null;
  const inProgress = status === 'pending' || status === 'verifying' || status === 'verified';

  useEffect(() => {
    if (!inProgress) return;

    let polls = 0;
    const MAX_POLLS = 40; // 40 x 30s = 20 minutes

    const timer = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(timer);
        setPollingStopped(true);
        return;
      }
      fetchStatus();
    }, 30_000);

    return () => clearInterval(timer);
  }, [inProgress, fetchStatus]);

  const handleSetDomain = async () => {
    const cleaned = domainInput.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/^www\./, '');

    if (!cleaned) return;

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await setCustomDomain(cleaned);
      setSuccess('Domain saved! Follow the DNS instructions below.');
      setShowDomainInput(false);
      setDomainInput('');
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set domain');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await verifyDomain();
      if (result.success) {
        setSuccess(result.message);
        setInstructions(null);
      } else {
        setError(result.message);
      }
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm('Are you sure you want to remove your custom domain?')) return;

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await removeDomain();
      setSuccess('Custom domain removed');
      setInstructions(null);
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove domain');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleToggleAutoRenew = async (domainId: string, currentValue: number) => {
    setTogglingAutoRenew(domainId);
    setError(null);
    setSuccess(null);

    try {
      const newValue = currentValue !== 1;
      await updateDomainAutoRenew(domainId, newValue);

      // Update local state
      setPurchasedDomains(prev =>
        prev.map(pd =>
          pd.id === domainId ? { ...pd, auto_renew: newValue ? 1 : 0 } : pd
        )
      );

      setSuccess(`Auto-renewal ${newValue ? 'enabled' : 'disabled'} for this domain.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update auto-renewal setting');
    } finally {
      setTogglingAutoRenew(null);
    }
  };

  const getProvisioningLabel = (status: string | null) => {
    switch (status) {
      case 'active':
        return { text: 'Connected', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' };
      case 'propagating':
        return { text: 'Setting up', color: 'bg-yellow-100 text-yellow-800', dotColor: 'bg-yellow-500' };
      case 'pending':
        return { text: 'Pending', color: 'bg-gray-100 text-gray-800', dotColor: 'bg-gray-500' };
      case 'failed':
        return { text: 'Setup issue', color: 'bg-red-100 text-red-800', dotColor: 'bg-red-500' };
      default:
        return { text: 'Unknown', color: 'bg-gray-100 text-gray-600', dotColor: 'bg-gray-400' };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="h-4 bg-gray-200 rounded w-32" />
          <div className="h-4 bg-gray-200 rounded w-48" />
        </div>
      </div>
    );
  }

  if (!domain) return null;

  const isConnected = domain.verificationStatus === 'connected';
  const isPending = domain.verificationStatus === 'pending';
  const isVerifying = domain.verificationStatus === 'verifying';
  // DNS is correct and the domain is attached to Cloudflare, which is still
  // validating it and issuing the certificate. The cron promotes this to
  // 'connected' on its own, so the customer does not need to click again.
  const isAwaitingCloudflare = domain.verificationStatus === 'verified';

  // A domain bought through niftee is provisioned for the customer — they have
  // no DNS records to add and nothing to check. The custom-domain endpoint
  // reports it like any other custom domain, so without this the same domain
  // appeared twice: "Connected" under Your Domains and "Pending DNS" under
  // Bring Your Own Domain, telling the customer to act on something we handle.
  const isNifteePurchasedDomain = !!domain.customDomain && purchasedDomains.some(
    (pd) => pd.domain_name.toLowerCase() === domain.customDomain!.toLowerCase()
  );

  // Only show the BYOD panel when there is genuinely a domain to bring, or the
  // customer has already brought one.
  const showByodPanel = !isNifteePurchasedDomain;

  return (
    <div className="space-y-6">
      {/* Purchased Domains */}
      {purchasedDomains.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Your Domains</h3>

          <div className="space-y-3">
            {purchasedDomains.map((pd) => {
              const label = getProvisioningLabel(pd.ns_propagation_status);
              return (
                <div
                  key={pd.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">{pd.domain_name}</span>
                    {pd.expires_at && (
                      <span className="ml-2 text-xs text-gray-500">
                        Renews {new Date(pd.expires_at).toLocaleDateString()}
                      </span>
                    )}
                    {pd.ns_propagation_status === 'propagating' && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Setting up your domain — usually takes 15-60 minutes
                      </p>
                    )}
                    {pd.ns_propagation_status === 'failed' && (
                      <p className="text-xs text-red-600 mt-0.5">
                        There was an issue setting up your domain. Please contact support.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Auto-renew toggle */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-gray-500">Auto-renew</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={pd.auto_renew === 1}
                        disabled={togglingAutoRenew === pd.id}
                        onClick={() => handleToggleAutoRenew(pd.id, pd.auto_renew)}
                        className={`
                          relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent
                          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          ${pd.auto_renew === 1 ? 'bg-blue-600' : 'bg-gray-200'}
                          ${togglingAutoRenew === pd.id ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
                        `}
                      >
                        <span
                          className={`
                            pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                            transition duration-200 ease-in-out
                            ${pd.auto_renew === 1 ? 'translate-x-4' : 'translate-x-0'}
                          `}
                        />
                      </button>
                    </label>
                    {/* Status badge */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${label.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${label.dotColor}`} />
                      {label.text}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* BYOD Domain Management */}
      {showByodPanel && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {purchasedDomains.length > 0 ? 'Bring Your Own Domain' : 'Your Domain'}
          </h3>
          {isConnected && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Connected
            </span>
          )}
          {isPending && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
              Pending DNS
            </span>
          )}
        </div>

        {/* Feedback messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
            {success}
          </div>
        )}

        {/* Current subdomain */}
        {domain.subdomain && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Free subdomain:</span>{' '}
            <a
              href={domain.subdomain}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {domain.subdomain.replace('https://', '')}
            </a>
          </div>
        )}

        {/* Connected domain */}
        {isConnected && domain.customDomain && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm">
              <span className="font-medium text-green-800">Custom domain:</span>{' '}
              <a
                href={`https://${domain.customDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-700 hover:underline font-medium"
              >
                {domain.customDomain}
              </a>
            </div>
            <button
              onClick={handleRemove}
              disabled={actionLoading}
              className="mt-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
            >
              Remove custom domain
            </button>
          </div>
        )}

        {/* Pending domain - DNS instructions */}
        {(isPending || isVerifying || isAwaitingCloudflare) && domain.customDomain && (
          <div className="space-y-3">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm font-medium text-yellow-800 mb-1">
                {domain.customDomain}
              </p>
              <p className="text-xs text-yellow-700">
                {isAwaitingCloudflare
                  ? 'Your DNS looks right and the domain is connected to Cloudflare. The security certificate is being issued now — this usually takes a few minutes and needs nothing further from you.'
                  : 'Add the DNS records below at your domain registrar, then click "Check DNS".'}
                {pollingStopped && (
                  <span className="block mt-1 font-medium">
                    Still working on it — refresh this page to check again, or contact us if
                    it has been more than a few hours.
                  </span>
                )}
              </p>
            </div>

            {instructions && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">DNS Records to Add</p>

                {/* CNAME record */}
                <div className="bg-white rounded border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase">CNAME Record</span>
                    <button
                      onClick={() => copyToClipboard(instructions.cname.target, 'cname')}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {copied === 'cname' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Name:</span>{' '}
                      <span className="font-mono font-medium">{instructions.cname.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Target:</span>{' '}
                      <span className="font-mono font-medium text-blue-700">{instructions.cname.target}</span>
                    </div>
                  </div>
                </div>

                {/* Apex forwarding — a registrar setting, not a DNS record.
                    Shown alongside the CNAME because the customer needs both
                    for the bare domain to reach the site. */}
                {instructions.apex && (
                  <div className="bg-white rounded border border-gray-200 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Domain Forwarding
                      </span>
                      <button
                        onClick={() => copyToClipboard(instructions.apex.to, 'apex')}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {copied === 'apex' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Forward:</span>{' '}
                        <span className="font-mono font-medium">{instructions.apex.from}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">To:</span>{' '}
                        <span className="font-mono font-medium text-blue-700">
                          {instructions.apex.to}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Steps */}
                <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                  {instructions.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleVerify}
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Checking...' : 'Check DNS'}
              </button>
              <button
                onClick={handleRemove}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        )}

        {/* No custom domain - show option to add one */}
        {!domain.customDomain && !showDomainInput && (
          <button
            onClick={() => setShowDomainInput(true)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            Connect your own domain
          </button>
        )}

        {/* Domain input form */}
        {!domain.customDomain && showDomainInput && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter your domain
              </label>
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="mybusiness.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && handleSetDomain()}
              />
              <p className="mt-1 text-xs text-gray-500">
                Don't include https:// or www
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSetDomain}
                disabled={actionLoading || !domainInput.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Saving...' : 'Set Domain'}
              </button>
              <button
                onClick={() => {
                  setShowDomainInput(false);
                  setDomainInput('');
                }}
                className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
